<template>
  <div :class="b()">
    <div
      :class="b('group-header')"
      @click="expanded = !expanded"
    >
      <div :class="b('group-headline')">
        <span :class="b('group-name')">
          {{ filterGroup.label }}
        </span>
        <span
          v-if="selectedCount"
          :class="b('selected-count')"
        >
          {{ selectedCount }}
        </span>
      </div>
      <e-icon
        :class="b('collapse-indicator', { expanded })"
        icon="i-chevron--down"
        size="14"
        inline
      />
      <div
        v-if="!!filterGroup.tooltip"
        :class="b('description-icon')"
      >
        <c-tooltip
          :popper-options="tooltipOptions"
          :teleport-to="$viewport.isMd ? 'body' : '#c-filter-modal'"
        >
          <button
            type="button"
            :aria-label="$t('c-filter-group-checkbox.filter-tooltip')"
          >
            <e-icon
              icon="i-question--circle"
              size="18"
              inline
            />
          </button>

          <template #tooltip>
            {{ filterGroup.tooltip }}
          </template>
        </c-tooltip>
      </div>
    </div>
    <collapse :when="expanded">
      <div
        v-if="!!filterGroup.description"
        :class="b('description-container', { 'has-tooltip': !!filterGroup.tooltip })"
      >
        <span :class="b('description')">
          {{ filterGroup.description }}
        </span>
      </div>
      <template v-for="filterItem in paginatedFilters">
        <e-checkbox
          v-if="filterItem.count"
          v-model="filterItem.selected"
          :key="filterItem.value"
          :class="b('checkbox')"
          :value="filterItem.value"
          :name="filterItem.value"
          @change="onChange"
        >
          <span :class="b('label')">
            {{ filterItem.label }}
          </span>
          <span :class="b('count')">
            {{ `(${filterItem.count})` }}
          </span>
        </e-checkbox>
      </template>
      <e-button
        v-if="showMoreAvailable"
        :class="b('show-more-button')"
        type="button"
        variant="text-underline"
        @click="toggleShowMore"
      >
        {{ showMoreButtonLabel }}
      </e-button>
    </collapse>
  </div>
</template>

<script lang="ts">
  import { Options } from '@popperjs/core';
  import { PropType, defineComponent } from 'vue';
  import { Collapse } from 'vue-collapsed';
  import eButton from '@/elements/e-button.vue';
  import eCheckbox from '@/elements/e-checkbox.vue';
  import eIcon from '@/elements/e-icon.vue';
  import cTooltip from '@/plugins/tooltip/c-tooltip.vue';
  import { Filter, FilterGroup } from '@/types/filter';

  // type Setup = {};

  type Data = {
    selectedValues: string[];
    expanded: boolean;
    showMore: boolean;
  };

  const MAX_INITIAL_FILTERS_DISPLAYED = 10;

  /**
   * Renders a filter of the type multi-select checkbox.
   */
  export default defineComponent({
    name: 'c-filter-group-checkbox',
    components: {
      cTooltip,
      eIcon,
      eButton,
      eCheckbox,
      collapse: Collapse,
    },

    props: {
      /**
       * Expects a filter object.
       */
      filterGroup: {
        type: Object as PropType<FilterGroup>,
        required: true,
      },

      /**
       * Define if the items should be expanded by default.
       */
      isExpandedByDefault: {
        type: Boolean,
        default: true,
      },
    },
    emits: {
      updateFilter: (): boolean => true,
    },

    // setup(): Setup {},
    data(): Data {
      return {
        selectedValues: this.filterGroup.values?.filter((item) => item.selected).map((item) => item.value) || [],
        expanded: this.isExpandedByDefault || this.filterGroup?.hasSelectedValues || false,
        showMore: false,
      };
    },

    computed: {
      tooltipOptions(): Partial<Options> {
        return {
          placement: 'top',
        };
      },

      filteredValues(): Filter[] {
        return this.filterGroup.values.filter((item) => item.count > 0);
      },

      paginatedFilters(): Filter[] {
        return this.showMore ? this.filteredValues : this.filteredValues.slice(0, MAX_INITIAL_FILTERS_DISPLAYED);
      },

      showMoreAvailable(): boolean {
        return this.filteredValues.length > MAX_INITIAL_FILTERS_DISPLAYED;
      },

      showMoreButtonLabel(): string {
        return this.showMore
          ? this.$t('c-filter-group-checkbox.show-less')
          : this.$t('c-filter-group-checkbox.show-more');
      },

      selectedCount(): number {
        return this.selectedValues.length;
      },
    },
    watch: {
      filterGroup(newFilterGroup: FilterGroup): void {
        this.selectedValues = newFilterGroup.values?.filter((item) => item.selected).map((item) => item.value) || [];
        this.expanded = this.isExpandedByDefault || this.filterGroup?.hasSelectedValues || false;
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onChange(): void {
        this.$emit('updateFilter');
      },

      toggleShowMore(): void {
        this.showMore = !this.showMore;
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-filter-group-checkbox {
    user-select: none;

    &__group-header {
      position: relative;
      cursor: pointer;
      margin-bottom: 1rem;
    }

    &__group-headline {
      display: flex;
      gap: 0.5rem;
      padding-right: 3rem;
      align-items: center;
    }

    &__group-name {
      @include mixins.heading-h4;
    }

    &__selected-count {
      width: 1rem;
      height: 1rem;
      border: 1px solid var(--theme-color-grayscale--600);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: variables.$font-size--smallest;
      line-height: normal;
    }

    &__description-container {
      margin-bottom: 1.5rem;

      &--has-tooltip {
        padding-right: 1.5rem;
        position: relative;
      }
    }

    &__description {
      @include mixins.heading-h6;
    }

    &__description-icon {
      position: absolute;
      right: 1.5rem;
      top: 2px;
    }

    &__count {
      color: variables.$va-color-grayscale--100;
      font-size: variables.$font-size--smallest;
      margin-left: 0.25rem;
    }

    &__label {
      margin-right: 0.15rem;
    }

    &__collapse-indicator {
      position: absolute;
      right: 0;
      top: 4px;
      display: flex;
      align-items: center;
      justify-content: end;

      &--expanded {
        transform: rotate(180deg);
      }
    }

    &__checkbox {
      align-items: flex-start;

      .e-checkbox__indicator {
        margin-top: 1px; // Fix for the border size.
      }

      &:not(:last-child) {
        margin-bottom: 0.5rem;
      }
    }
  }
</style>
